import * as React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { TLD } from '@packages/common';
import { Flex, Button, Icon, Anchor, Tabs, TabPanel, useTab, themeGet } from '@packages/ui';
import { OffCanvasContext } from '@packages/ui';
import { useFastlane } from '@packages/shared';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';

import { NavigationLi, NavigationUl } from './MobileNavigationStyleComponents';
import { MobileNavigationCategoriesTab } from './MobileNavigationCategoriesTab';
import { LinkItem } from '../Navigation';
import NavigationTabs from '../NavigationTabs';
import mainNavigation from '../../layoutConfigs/main_navigation.json';
import { getInspirationItems, getMobileNavigationTabs } from '../../constants';

export const MOBILE_NAVIGATION_WIDTH = '300px';

const MobileNavigationWrapper = styled.div`
  height: 100vh;
  width: ${MOBILE_NAVIGATION_WIDTH};
  background-color: ${themeGet.colors('white')};
`;

const NavigationTabsWrapper = styled.div`
  padding-left: ${themeGet.space(2)};
  border-bottom: 1px solid ${themeGet.colors('potPourri')};
`;

const LinkItemStyled = styled(LinkItem)`
  cursor: pointer;
  font-size: ${themeGet.fontSize(5)};
`;

const TabsUl = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0 ${themeGet.space(2)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #cccccc;
`;

const MobileNavigationTab: React.FC<{ tabId: string; item: any }> = ({ tabId, item }) => {
  const { isActive, onClick } = useTab(tabId);

  return (
    <LinkItemStyled isActive={isActive} onClick={onClick} isCurrent={false} emphesize={false}>
      <Anchor>{item.title}</Anchor>
    </LinkItemStyled>
  );
};

/**
 * TMP Black Days stuff START
 *
 * note that DE and FR have different start end end days. After black days are over change back to:
 * `<a href={item.as}>{item.title}</a>` again
 */
function isDuringBlackDays(tld: TLD) {
  const blackDaysStart = tld === TLD.DE ? new Date('11/20/2020') : new Date('11/27/2020');
  const blackDaysEnd = tld === TLD.DE ? new Date('12/01/2020') : new Date('12/08/2020');
  const now = new Date();
  return now >= blackDaysStart && now < blackDaysEnd;
}

function isSaleNavItem(title: string) {
  return title.trim().toLowerCase() === 'sale' || title.trim().toLocaleLowerCase() === 'promos';
}

const TEMPBlackDaysNavItem: React.FC<{ item: { title: string; as: string }; tld: TLD }> = ({ item, tld }) => {
  if (isSaleNavItem(item.title) && isDuringBlackDays(tld)) {
    return (
      <a href={item.as} style={{ fontWeight: 600, color: 'black' }}>
        Black Days
      </a>
    );
  }

  return <a href={item.as}>{item.title}</a>;
};

/** TEMP Black Days changes END */

const APP_TLD = getPublicRuntimeConfig('APP_TLD');
const ROUTES = getPublicRuntimeConfig('ROUTES');

export const MobileNavigation: React.FC = () => {
  const client = useFastlane();
  const { toggle } = React.useContext(OffCanvasContext);

  const { fetchSubCategories } = client;

  const tabs = getMobileNavigationTabs(ROUTES, APP_TLD);
  const inspirationItems = getInspirationItems(APP_TLD);
  const [sortiment, inspiration] = tabs;
  return (
    <MobileNavigationWrapper>
      <Flex justifyContent="flex-end" px={[2]}>
        <Button variant="clean" onClick={() => toggle(false)} data-testid="mobile-navigation:close-button">
          <Icon name="close" color="sandDune" width="16px"></Icon>
        </Button>
      </Flex>
      {APP_TLD === TLD.DE && (
        <NavigationTabsWrapper>
          <NavigationTabs />
        </NavigationTabsWrapper>
      )}
      <Tabs activeTabId={sortiment.id}>
        <TabsUl>
          {tabs.map((item) =>
            item.href ? (
              <LinkItemStyled key={item.id}>
                <Link prefetch={false} href={item.href}>
                  <Anchor>{item.title}</Anchor>
                </Link>
              </LinkItemStyled>
            ) : (
              <MobileNavigationTab key={item.id} tabId={item.id} item={item} />
            ),
          )}
        </TabsUl>
        <TabPanel tabId={sortiment.id}>
          <MobileNavigationCategoriesTab
            mainCategories={mainNavigation.main as any}
            fetchSubCategories={fetchSubCategories}
          />
        </TabPanel>
        <TabPanel tabId={inspiration.id}>
          <NavigationUl>
            {inspirationItems.map((item) => (
              <NavigationLi key={item.title} emphesize={item.emphesize}>
                <Link prefetch={false} href={item.href}>
                  <TEMPBlackDaysNavItem item={item} tld={APP_TLD} />
                </Link>
              </NavigationLi>
            ))}
          </NavigationUl>
        </TabPanel>
      </Tabs>
    </MobileNavigationWrapper>
  );
};
